import { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapUnderline from '@tiptap/extension-underline';
import TiptapImage from '@tiptap/extension-image';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Code, Link2 } from 'lucide-react';

// #1684 BR-58 P-A — Inline-Bild-Gates (identisch zur Case-Antwort-Baseline #1676):
// erlaubte Formate + Max-Größe (10 MB) für Paste/Drop.
const INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const INLINE_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

/**
 * RichTextEditor — Tiptap-Wrapper mit Standard-Toolbar (Bold/Italic/Underline/UL/OL/Link/Code).
 *
 * Extrahiert aus Cases.jsx TiptapEditor (#1131, #1247 DRY).
 *
 * Verwendung:
 *  - Communication-Tab Reply in Cases (always-on Toolbar mit Submit-Hotkey)
 *  - Neuer-Case-Modal Notiz-Feld (#1247)
 *  - Andere Module die HTML-Notizen brauchen
 *
 * Props:
 *  - value (string, HTML): controlled HTML-Wert
 *  - onChange (fn): wird bei Update aufgerufen mit dem aktuellen HTML
 *  - placeholder (string)
 *  - onSubmit (fn, optional): Ctrl/Cmd+Enter triggert dies
 *  - isInternal (bool): Amber-Background für interne Notiz (Cases-Reply-Variante)
 *  - toolbarMode ('focus' | 'always', default 'always'): focus blendet Toolbar nur bei Fokus/Inhalt ein
 *  - minHeight (px, default 80)
 *  - maxHeight (px, default 400)
 *  - resizable (bool, default false, #1686): Drag-Griff unter dem Content (Griff-/Cap-Logik
 *    aus #1679/#1680: Cap = max(200, 70% Viewport), Min = minHeight, Doppelklick = Auto-Reset).
 *    Richtung hier Drag-NACH-UNTEN = größer (Editor sitzt im scrollbaren Dialog, nicht am
 *    Viewport-Boden wie der Reply-Composer — die #1680-Invertierung ist hier nicht nötig).
 *  - tooltips (obj, optional): Toolbar-Button-Tooltips { bold, italic, underline, bulletList, orderedList, link, code, resize }
 *
 * Inline-Bilder (#1684 BR-58 P-A, OPT-IN — Default AUS, kein Consumer nutzt das bisher):
 *  - enableInlineImages (bool, default false): aktiviert Image-Extension + Paste/Drag von
 *    Bildern in den Fließtext. Bei false existiert der img-Node nicht im Schema und es
 *    werden KEINE Paste/Drop-Handler registriert → Verhalten byte-identisch wie vor #1684.
 *  - onInlineImageUpload (async fn(file) → { src, alt? }): Pflicht bei enableInlineImages.
 *    Lädt die Datei hoch und liefert die served URL (z.B. /api/cases/attachments/<id>/inline).
 *  - onInlineImageError (fn(reason, file)): reason = 'type' | 'size' | 'upload' — Consumer
 *    übersetzt in i18n-Toast (Editor selbst ist i18n-frei).
 *  - onNonImageFiles (fn(files)): Nicht-Bild-Dateien aus einem Drop (z.B. → normale Anhänge).
 *  - onDropConsumed (fn): Drop wurde vom Editor konsumiert (Parent-Dropzone nicht doppelt feuern).
 *
 *  WICHTIG für Consumer, deren Inhalt (auch) an OMS/Odoo geschrieben werden kann: JEDER
 *  OMS-Write dieses Feldes MUSS serverseitig den img-Strip anwenden (#1678-Muster:
 *  OMS_SANITIZE_CONFIG/stripImgsForOms) — sonst landen relative img-URLs im Odoo-Backend
 *  (kaputtes Bild). Kunden-Mail-Pfade nutzen den CID-Helper (#1684) bzw. sendCaseReplyEmail.
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = '',
  onSubmit,
  isInternal = false,
  toolbarMode = 'always',
  minHeight = 80,
  maxHeight = 400,
  resizable = false,
  tooltips = {},
  enableInlineImages = false,
  onInlineImageUpload,
  onInlineImageError,
  onNonImageFiles,
  onDropConsumed,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  // #1684 — handlePaste/handleDrop-Closures entstehen VOR dem editor-Objekt → Ref (wie #1676)
  const editorRefLocal = useRef(null);
  // #1686 — per Drag-Griff gezogene Höhe; null = Auto-Verhalten (minHeight/maxHeight-Props)
  const [dragHeight, setDragHeight] = useState(null);
  const contentWrapRef = useRef(null);

  // #1686 — vertikaler Resize (Cap-Logik aus #1679): min = minHeight-Prop, Cap ~70% Viewport.
  // Drag NACH UNTEN = größer; Doppelklick auf den Griff = zurück auf Auto.
  const startResizeDrag = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = contentWrapRef.current ? contentWrapRef.current.getBoundingClientRect().height : minHeight;
    const maxH = Math.max(200, Math.round(window.innerHeight * 0.7));
    const onMove = (ev) => setDragHeight(Math.min(maxH, Math.max(minHeight, Math.round(startH + (ev.clientY - startY)))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // #1682-Größenlogik — logische Anzeigegröße bestimmen: HiDPI-Screenshots haben die
  // doppelte intrinsische Pixelbreite → width = naturalWidth / devicePixelRatio.
  const readLogicalWidth = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const im = new window.Image();
    im.onload = () => {
      const dpr = window.devicePixelRatio || 1;
      URL.revokeObjectURL(url);
      resolve(Math.round(im.naturalWidth / dpr) || null);
    };
    im.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    im.src = url;
  });

  // #1684 — gemeinsamer Handler für Paste + Drop: validieren, via Consumer-Callback hochladen,
  // als <img src=… width=…> in den Fließtext einfügen (Spiegel der #1676-Baseline).
  const insertImageFiles = (editorInstance, files) => {
    for (const file of files) {
      if (!INLINE_IMAGE_TYPES.includes(file.type)) { onInlineImageError?.('type', file); continue; }
      if (file.size > INLINE_IMAGE_MAX_BYTES) { onInlineImageError?.('size', file); continue; }
      Promise.all([onInlineImageUpload?.(file), readLogicalWidth(file)]).then(([res, logicalWidth]) => {
        if (res?.src) {
          editorInstance.chain().focus().insertContent({
            type: 'image',
            attrs: { src: res.src, alt: res.alt || file.name || '', width: logicalWidth },
          }).run();
        }
      }).catch((e) => onInlineImageError?.('upload', file, e));
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false }),
      TiptapLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
      TiptapUnderline,
      // #1684 — nur bei Opt-in; ohne Extension existiert der img-Node nicht im Schema.
      // width-Attribut im Schema, damit die Originalgröße (#1682) im HTML landet.
      ...(enableInlineImages ? [TiptapImage.extend({
        addAttributes() {
          return { ...this.parent?.(), width: { default: null } };
        },
      }).configure({ inline: true, allowBase64: false })] : []),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none overflow-y-auto px-4 py-3 text-sm focus:outline-none text-content${enableInlineImages ? ' [&_img]:max-w-[320px] [&_img]:rounded-[8px] [&_img]:border [&_img]:border-line' : ''}`,
        style: `min-height: ${minHeight}px; max-height: ${maxHeight}px;`,
      },
      handleKeyDown(view, event) {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          onSubmit?.();
          return true;
        }
        return false;
      },
      // #1684 — Paste/Drop-Handler NUR bei Opt-in registrieren → Default byte-identisch
      ...(enableInlineImages ? {
        handlePaste: (view, event) => {
          const files = Array.from(event.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
          if (!files.length) return false;
          event.preventDefault();
          insertImageFiles(editorRefLocal.current, files);
          return true;
        },
        handleDrop: (view, event) => {
          const all = Array.from(event.dataTransfer?.files || []);
          if (!all.length) return false;
          event.preventDefault();
          event.stopPropagation();
          onDropConsumed?.();
          const images = all.filter(f => f.type.startsWith('image/'));
          const others = all.filter(f => !f.type.startsWith('image/'));
          if (images.length) insertImageFiles(editorRefLocal.current, images);
          if (others.length) onNonImageFiles?.(others);
          return true;
        },
      } : {}),
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onUpdate: ({ editor }) => {
      setHasContent(!editor.isEmpty);
      onChange?.(editor.isEmpty ? '' : editor.getHTML());
    },
  });

  // #1684 — Editor-Instanz für insertImageFiles-Closures verfügbar machen
  useEffect(() => { editorRefLocal.current = editor; }, [editor]);

  // Sync external value changes (e.g. parent reset)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    // Avoid resetting cursor on every keystroke
    if (incoming !== current && !editor.isFocused) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const ToolBtn = ({ active, onClick, children, title }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`p-1 rounded transition-colors ${
        active
          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
          : 'text-content-tertiary hover:text-content hover:bg-surface-hover'
      }`}
    >
      {children}
    </button>
  );

  const showToolbar = toolbarMode === 'always' ? true : (isFocused || hasContent);

  return (
    <div
      className={`border rounded-card transition-colors focus-within:ring-2 focus-within:ring-[#338edc]/10 ${
        isInternal
          ? 'bg-amber-500/5 border-amber-500/20 focus-within:border-amber-500/40'
          : 'bg-white dark:bg-surface-inset border-[var(--border-default,#E5E7EB)] dark:border-line focus-within:border-[#338edc]'
      }`}
      onKeyDown={e => e.stopPropagation()}
    >
      <div
        className={`flex items-center gap-0.5 px-2 overflow-hidden transition-all duration-150 ease-out ${
          showToolbar
            ? 'max-h-10 py-1 opacity-100 border-b border-line/50'
            : 'max-h-0 py-0 opacity-0 border-b-0'
        }`}
      >
        <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title={tooltips.bold || 'Bold'}>
          <Bold className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title={tooltips.italic || 'Italic'}>
          <Italic className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title={tooltips.underline || 'Underline'}>
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolBtn>
        <span className="w-px h-4 bg-line mx-1" />
        <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title={tooltips.bulletList || 'Bullet list'}>
          <List className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title={tooltips.orderedList || 'Ordered list'}>
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolBtn>
        <span className="w-px h-4 bg-line mx-1" />
        <ToolBtn
          active={editor.isActive('link')}
          onClick={() => {
            if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
            const url = window.prompt('URL');
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
          title={tooltips.link || 'Link'}
        >
          <Link2 className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title={tooltips.code || 'Inline code'}>
          <Code className="w-3.5 h-3.5" />
        </ToolBtn>
      </div>
      {/* #1686 — gezogene Höhe: Wrapper fix, ProseMirror füllt + scrollt. !important nötig,
          weil min/max-height als Inline-Style auf .ProseMirror gesetzt sind. */}
      <div ref={contentWrapRef}
        className={dragHeight ? 'overflow-hidden [&_.ProseMirror]:!h-full [&_.ProseMirror]:!min-h-0 [&_.ProseMirror]:!max-h-none' : ''}
        style={dragHeight ? { height: `${dragHeight}px` } : undefined}>
        <EditorContent editor={editor} className={dragHeight ? 'h-full' : ''} />
      </div>
      {resizable && (
        <div onMouseDown={startResizeDrag} onDoubleClick={() => setDragHeight(null)}
          title={tooltips.resize || 'Drag to resize'}
          className="flex items-center justify-center h-2.5 cursor-ns-resize select-none group/resize">
          <span className="w-8 h-1 rounded-full bg-[var(--border-default,#E5E7EB)] group-hover/resize:bg-[var(--text-muted,#9CA3AF)] transition-colors" />
        </div>
      )}
    </div>
  );
}
